import {
  Alert,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { CategorySelect } from './CategorySelect'
import { formatPercent, PAGE_SIZE } from './helpers'
import { useReviewQueue } from './useProductCategoryData'
import { useProductCategories } from '../../EditQuote/lines/productCategories'
import type { ReviewRow } from './ProductCategories.types'
import './ReviewQueueTab.css'

export function ReviewQueueTab() {
  const [page, setPage] = useState(0)
  const [choices, setChoices] = useState<Record<string, string>>({})
  const { rows, total, loading, error, saving, approve } = useReviewQueue(page)
  const categories = useProductCategories()
  const isAssignable = (name: string) => categories.some(category => category.name === name)

  useEffect(() => {
    if (!loading && rows.length === 0 && page > 0) setPage(prev => prev - 1)
  }, [loading, rows.length, page])

  const chosenCategory = (row: ReviewRow) =>
    row.validationCode in choices ? choices[row.validationCode] : row.productCategory

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Parts the system wasn&apos;t sure about. Approving saves the category for the part, updates its quote lines and
        is used on every future quote.
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {loading && <LinearProgress />}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Part</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Why</TableCell>
              <TableCell align="right">Lines</TableCell>
              <TableCell>Category</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.validationCode}>
                <TableCell>
                  <Typography variant="body2">{row.mfrPartNumberUpper}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.manufacturer}
                  </Typography>
                </TableCell>
                <TableCell className="review-queue-tab-description">{row.evidence?.description ?? '-'}</TableCell>
                <TableCell className="review-queue-tab-reason">
                  <Chip
                    size="small"
                    label={`${row.source} ${formatPercent(row.confidence)}`}
                    className="review-queue-tab-chip"
                  />
                  <Typography variant="caption">
                    {row.evidence?.ai?.rationale ?? row.evidence?.conflicts?.join('; ') ?? ''}
                  </Typography>
                </TableCell>
                <TableCell align="right">{row.lineCount}</TableCell>
                <TableCell>
                  <CategorySelect
                    value={chosenCategory(row)}
                    onChange={id => setChoices(prev => ({ ...prev, [row.validationCode]: id }))}
                  />
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={saving === row.validationCode || !isAssignable(chosenCategory(row))}
                    onClick={() => void approve(row.validationCode, chosenCategory(row))}
                  >
                    Approve
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>Nothing waiting for review.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        rowsPerPage={PAGE_SIZE}
        rowsPerPageOptions={[PAGE_SIZE]}
        onPageChange={(_e, next) => setPage(next)}
      />
    </Stack>
  )
}
